import axios from 'axios';
import { userOperations } from '../user';
import { appOperations } from '../app';
import { addHashIdToUserConfig } from '../user/selectors';
import * as Creators from './actions';
import { checkS3FileExists } from 'src/components/SignIn/utils';
import { redirectAdvertising } from 'src/utils/app';

export const { clearMessage, signInFailure, signInSuccess, signOutFailure, signOutSuccess, setErrorMessage } = Creators;

async function handleResponse(response, dispatch, { email }) {
  const { session } = response.data;
  const responseObject = {
    status: 'good',
    data: response.data
  };

  const responseWithHashId = addHashIdToUserConfig({ data: response.data });
  const appName = responseObject?.data?.appConfig?.domainConfig?.appName;
  const redirectToBeacon =
    appName === 'advertising' && redirectAdvertising(response.data.userConfig.vendor.BeaconClientId);

  if (session.status === 'bad') {
    if (session.statusMessage === 'passwordExpired') {
      responseObject.status = session.status;
      responseObject.redirect = `/password_reset?step=passwordExpired&username=${encodeURIComponent(email)}`;
      responseObject.redirectType = 'local';
      return responseObject;
    } else if (session.statusMessage === 'recaptchaFailed') {
      dispatch(signInFailure('Security verification failed. Please try again.'));
      responseObject.status = session.status;
      return responseObject;
    } else {
      dispatch(signInFailure('Invalid username or password.'));
      responseObject.status = session.status;
      return responseObject;
    }
  }

  if (session.status === 'super_user_not_allowed') {
    responseObject.status = 'bad';
    dispatch(signInFailure(session.statusMessage));
  } else if (session.hasValidBilling) {
    if (session.statusMessage === 'beaconRedirect' || redirectToBeacon) {
      responseObject.redirect = 'https://beacon.stackline.com';
      responseObject.redirectType = 'window';
    } else if (window.location.host.includes('enterprise-sso')) {
      responseObject.redirectType = 'window';
    } else {
      const isProfileImageUploaded = await checkS3FileExists(session.userId);

      if (!isProfileImageUploaded && !responseObject?.data?.userConfig?.skippedProfileImageUpload) {
        responseObject.isProfileImageUploaded = false;
      } else {
        responseObject.isProfileImageUploaded = true;
        dispatch(appOperations.receiveAppConfig(responseWithHashId));
        dispatch(userOperations.receiveUserConfig(responseWithHashId));
        dispatch(signInSuccess());
      }
      responseObject.redirect = '/';
      responseObject.redirectType = 'local';

      return responseObject;
    }
  } else if (session.statusMessage === 'trialExpired' || session.statusMessage === 'billingFailed') {
    if (session.isAdmin) {
      responseObject.redirect = '/subscribe';
      responseObject.redirectType = 'local';
      dispatch(userOperations.receiveUserConfig(responseWithHashId));
      dispatch(signInSuccess());
    } else {
      let message = '';
      if (session.statusMessage === 'trialExpired') {
        message = 'Free trial expired';
      } else if (session.statusMessage === 'billingFailed') {
        message = 'Billing failed';
      }
      responseObject.status = 'bad';
      dispatch(signInFailure(`${message}. Please contact ${session.adminEmail} about your stackline subscription.`));
    }
  } else if (session.statusMessage === 'accountLocked') {
    responseObject.status = 'bad';
    dispatch(signInFailure('Account is locked. Please contact support@stackline.com about your subscription.'));
  } else {
    responseObject.status = 'bad';
    dispatch(signInFailure('Billing failed. Please contact support@stackline.com about your subscription.'));
  }

  return responseObject;
}

/**
 * Issue a login API call using the given baseUrl and credentials
 */
const logInCall = (baseUrl, email, password, referrer = null, recaptchaToken = null, userAgent = null) =>
  axios.post(
    `https://${baseUrl}/api/user/Login`,
    {
      username: email,
      password,
      recaptchaToken,
      referrer,
      userAgent
    },
    {
      headers: {
        Authorization: `Basic ${btoa(`${email}:${password}`)}`
      }
    }
  );

/**
 * Issue a login API call using the given baseUrl and credentials
 */
const logInExternalCall = (
  baseUrl,
  username,
  accessToken,
  refreshToken,
  expiresIn,
  referrer = null,
  recaptchaToken = null,
  userAgent = null
) =>
  axios.post(
    `https://${baseUrl}/api/user/LoginExternalAuth`,
    {
      username,
      accessToken,
      refreshToken,
      expiresIn,
      referrer,
      recaptchaToken,
      userAgent
    },
    {
      headers: {}
    }
  );

/**
 * Issue a login API call using the given baseUrl and ignore any response or error
 */
export const logInFireAndForget = (baseUrl, email, password) =>
  logInCall(baseUrl, email, password)
    .then(() => { })
    .catch(() => { });

/**
 * Issue a login API call and handle its response or error
 */
export const logIn =
  (baseUrl, email, password, referrer = null, recaptchaToken = null, userAgent = null) =>
    (dispatch) =>
      logInCall(baseUrl, email, password, referrer, recaptchaToken, userAgent)
        .then((response) => handleResponse(response, dispatch, { email }))
        .catch((error) => {
          dispatch(signInFailure(error));
        });

export const logInExternal =
  (baseUrl, email, accessToken, refreshToken, expiresIn, referrer = null, recaptchaToken = null, userAgent = null) =>
    (dispatch) =>
      logInExternalCall(baseUrl, email, accessToken, refreshToken, expiresIn, referrer, recaptchaToken, userAgent)
        .then((response) => handleResponse(response, dispatch, { email }))
        .catch((error) => {
          dispatch(signInFailure(error));
        });

export const logOut = () => (dispatch) =>
  axios
    .get('/api/user/Logout')
    .then((response) => {
      dispatch(signOutSuccess());
      return response;
    })
    .catch((err) => {
      dispatch(signOutFailure(err));
      throw err;
    });

export const signUpByInvite = (requestBody) => (dispatch) =>
  axios
    .post('/api/user/UserSignupByInvite', requestBody)
    .then((response) => handleResponse(response, dispatch, { email: requestBody.email }))
    .catch((err) => {
      dispatch(signInFailure(err));
      throw err;
    });

export const getUserByInviteCode = (inviteCode) => () =>
  axios.post(`/api/user/GetUserByInviteCode?code=${inviteCode}&rid=1`);

export const resetPassword = (email) => () => axios.post(`/api/user/SendPasswordReset?email=${email}`);

export const createNewPassword = (requestBody) => () => axios.post('/api/user/ResetPassword', requestBody);
